import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";

// Cached promise so we don't hit /realestate/locations on every mount.
let _cache = { lang: null, data: null };
async function fetchLocations(lang) {
  if (_cache.lang === lang && _cache.data) return _cache.data;
  const res = await api.get(`/realestate/locations?lang=${lang}`);
  _cache = { lang, data: res.data };
  return res.data;
}

/**
 * Cascading Country → State → City selector.
 * Controlled by a single `value` (the city name string) + `onChange(city)`.
 * - Pre-resolves country/state from the supplied city when possible.
 * - Layout: 3 stacked selects when `direction="stack"` (default), or inline.
 */
export default function LocationSelector({
  value,
  onChange,
  testidPrefix = "loc",
  optional = false,
  compact = false,
}) {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [data, setData] = useState({ countries: [] });
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");

  // Load locations once per language
  useEffect(() => {
    let alive = true;
    fetchLocations(lang).then((d) => { if (alive) setData(d); });
    return () => { alive = false; };
  }, [lang]);

  // When external value changes, find the matching country + state
  useEffect(() => {
    if (!value || !data.countries.length) {
      // keep current country/state if already chosen
      return;
    }
    for (const c of data.countries) {
      for (const s of c.states) {
        if (s.cities.some((ci) => ci.city === value)) {
          setCountry(c.code);
          setState(s.name);
          return;
        }
      }
    }
  }, [value, data]);

  const states = useMemo(() => {
    return data.countries.find((c) => c.code === country)?.states || [];
  }, [data, country]);

  const cities = useMemo(() => {
    return states.find((s) => s.name === state)?.cities || [];
  }, [states, state]);

  const onCountry = (e) => {
    const v = e.target.value;
    setCountry(v);
    setState("");
    onChange("");
  };
  const onState = (e) => {
    const v = e.target.value;
    setState(v);
    onChange("");
  };
  const onCity = (e) => {
    onChange(e.target.value);
  };

  const labelCountry = pt ? "País" : "Country";
  const labelState = pt ? "Estado / Região" : "State / Region";
  const labelCity = pt ? "Cidade" : "City";
  const placeholderCountry = pt ? "Selecione o país" : "Select country";
  const placeholderState = pt ? "Selecione o estado / região" : "Select state / region";
  const placeholderCity = pt ? "Selecione a cidade" : "Select city";

  const sel = `w-full bg-black/40 border border-white/10 rounded-md ${compact ? "px-2 py-1.5" : "px-3 py-2"} text-sm focus:outline-none focus:border-gold-500 disabled:opacity-50`;
  const lbl = "text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-mono block mb-1";

  return (
    <div className="space-y-2.5" data-testid={`${testidPrefix}-selector`}>
      <div>
        <label className={lbl}>{labelCountry}</label>
        <select
          value={country}
          onChange={onCountry}
          className={sel}
          data-testid={`${testidPrefix}-country`}
        >
          <option value="">{placeholderCountry}</option>
          {data.countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} {c.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={lbl}>{labelState}</label>
        <select
          value={state}
          onChange={onState}
          disabled={!country}
          className={sel}
          data-testid={`${testidPrefix}-state`}
        >
          <option value="">
            {country ? placeholderState : (pt ? "— escolha o país primeiro —" : "— pick country first —")}
          </option>
          {states.map((s) => (
            <option key={s.name} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={lbl}>
          {labelCity}{optional && <span className="text-neutral-600"> {pt ? "(opcional)" : "(optional)"}</span>}
        </label>
        <select
          value={value || ""}
          onChange={onCity}
          disabled={!state}
          className={sel}
          data-testid={`${testidPrefix}-city`}
        >
          <option value="">
            {state ? placeholderCity : (pt ? "— escolha o estado primeiro —" : "— pick state first —")}
          </option>
          {cities.map((ci) => (
            <option key={ci.city} value={ci.city}>
              {ci.city}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
